import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { IRuntimeEnvironment, RequestConfigProperties } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { Identity, IdentityStorage, SessionInfoStorage } from 'storage-service-lib';
import { AbstractAuthService } from './auth.service';
import { currentUser } from './current-user';

@Injectable({ providedIn: 'root' })
export class SalesforceAuthService extends AbstractAuthService {
  constructor(
    identityStorage: IdentityStorage,
    http: HttpClient,
    serviceProvider: SharedServiceProviderServiceFactory,
    private sessionInfoStorage: SessionInfoStorage
  ) {
    super(identityStorage, http, serviceProvider);
    console.log('SalesforceAuthService created.');
  }

  getRequestProperties(req: HttpRequest<any>, token: string): RequestConfigProperties | undefined {
    const decoded = jwtDecode<any>(token);
    if (req.url.includes(decoded.instanceUrl)) {
      return { setHeaders: { Authorization: 'Bearer ' + decoded.accessToken } };
    }
    return undefined;
  }

  override getTenantId(jwt: string) {
    const decoded = jwtDecode<any>(jwt);
    return decoded["organizationId"];
  }

  protected getNamespace(): Namespace {
    return Namespace.salesforce;
  }

  protected getRequestAuthenticationHostUrl(): string {
    const callbackUrl = `${this.environment.uiHost}/callback?instance=${this.instanceId}`;
    const envParam = this.getSalesforceEnvParam();
    const envSuffix = envParam ? `&env=${envParam}` : '';
    return `${this.environment.kpHost}/auth/salesforce/host?targetCallback=${encodeURIComponent(callbackUrl)}${envSuffix}`;
  }

  protected override postTokenAction(jwt: string): void {
    const identity = <Identity>this.identityStorage.get();
    identity.tenant = jwtDecode<any>(jwt).organizationName;
    this.identityStorage.put(identity);
    if (this.runtimeEnvironment) {
      this.setCurrentUser(jwt);
      this.setClientName(jwt);
    }
  }

  private setCurrentUser(jwt: string): void {
    const decoded = jwtDecode<any>(jwt);
    currentUser.userInfo = {
      id: decoded.userId,
      name: decoded.name,
      email: decoded.email,
    };
  }

  private setClientName(jwt: string): void {
    const jwtPayload = jwtDecode<any>(jwt);
    const runtimeEnvironment = this.runtimeEnvironment as IRuntimeEnvironment;
    runtimeEnvironment.client.name = jwtPayload.organizationName;
    console.debug(`Setting client name from token to ${runtimeEnvironment.client.name}.`);
  }

  private getSalesforceEnvParam(): string | undefined {
    const url = this.sessionInfoStorage.get()?.signInUrl || this.runtimeEnvironment?.origin?.url || window.location.href;
    try {
      const env = new URL(url).searchParams.get('env');
      return env?.toLowerCase() === 'sandbox' ? 'sandbox' : undefined;
    } catch {
      return undefined;
    }
  }
}
